import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Payload from the Tap to Pay Android WebView (`NoDeepLinkFragment.runPaymentSuccessJavaScript`).
 * Matches `window.onTapToPayAndroidPaymentResult` / `CustomEvent('taptopay_android')`.
 */
export interface TapToPayAndroidPaymentDetail {
  status: string;
  payment_intent_id?: string;
  order_id?: string;
  /** Deep-link `id` query param (internal order id), when distinct from `order_id`. */
  internal_id?: string;
  message?: string;
}

declare global {
  interface Window {
    onTapToPayAndroidPaymentResult?: (detail: TapToPayAndroidPaymentDetail) => void;
  }
}

/**
 * Bridges native Android WebView → Angular: instant payment success without waiting on Pusher.
 */
@Injectable({ providedIn: 'root' })
export class TapToPayAndroidBridgeService {
  private readonly resultsSubject = new Subject<TapToPayAndroidPaymentDetail>();
  readonly nativePaymentResults$: Observable<TapToPayAndroidPaymentDetail> =
    this.resultsSubject.asObservable();

  private previousWindowHook: typeof window.onTapToPayAndroidPaymentResult | undefined;
  private boundListener: ((ev: Event) => void) | null = null;
  private lastDedupKey = '';
  private lastDedupAt = 0;

  constructor(private readonly zone: NgZone) {
    this.install();
  }

  private normalize(raw: any): TapToPayAndroidPaymentDetail {
    const o = raw as Record<string, any> | null | undefined;
    return {
      status: String(o?.['status'] ?? ''),
      payment_intent_id: o?.['payment_intent_id'] != null ? String(o['payment_intent_id']) : '',
      order_id: o?.['order_id'] != null ? String(o['order_id']) : '',
      internal_id: o?.['internal_id'] != null ? String(o['internal_id']) : '',
      message: o?.['message'] != null ? String(o['message']) : '',
    };
  }

  private emitDeduped(detail: TapToPayAndroidPaymentDetail): void {
    const key = `${detail.status}|${detail.order_id}|${detail.internal_id}|${detail.payment_intent_id}|${detail.message}`;
    const now = Date.now();
    if (key === this.lastDedupKey && now - this.lastDedupAt < 400) {
      return;
    }
    this.lastDedupKey = key;
    this.lastDedupAt = now;
    this.zone.run(() => this.resultsSubject.next(detail));
  }

  private install(): void {
    const forward = (raw: any): void => {
      const detail = this.normalize(raw);
      if (detail.status === 'success' || detail.status === 'failed') {
        this.emitDeduped(detail);
      }
    };

    this.previousWindowHook = window.onTapToPayAndroidPaymentResult;
    window.onTapToPayAndroidPaymentResult = (detail: TapToPayAndroidPaymentDetail) => {
      try {
        this.previousWindowHook?.(detail);
      } catch {
        /* ignore third-party hook errors */
      }
      forward(detail);
    };

    this.boundListener = (ev: Event) => {
      const ce = ev as CustomEvent<any>;
      forward(ce.detail);
    };
    window.addEventListener('taptopay_android', this.boundListener);
  }
}
